import { useCallback } from 'react';
import {
  SearchSelectField,
  type SearchSelectOption,
  type SearchSelectPage,
} from '@/components/ui';
import { adminFetch } from '@/lib/apiClient';

const PAGE_SIZE = 20;

interface TemplateView {
  templateId: string;
  templateCode: string;
  channel: string;
  languageCode: string;
  subject?: string | null;
  isAdvertising: boolean;
  createdAt: string;
}

interface ListEnvelope {
  success: boolean;
  data?: {
    items?: TemplateView[];
    totalElements?: number;
    totalPages?: number;
    page?: number;
    size?: number;
  } | null;
}

const toOption = (t: TemplateView): SearchSelectOption => ({
  value: t.templateCode,
  label: t.templateCode,
  hint:
    [t.channel, t.languageCode, t.isAdvertising ? '광고' : null]
      .filter(Boolean)
      .join(' · ') || undefined,
});

interface Props {
  channel?: string;
  value: string | null;
  onChange: (templateCode: string | null) => void;
  placeholder?: string;
}

export const NotificationTemplateSearchSelect = ({
  channel,
  value,
  onChange,
  placeholder,
}: Props) => {
  const loadOptions = useCallback(
    async (keyword: string, page: number): Promise<SearchSelectPage> => {
      const qs = new URLSearchParams({
        size: String(PAGE_SIZE),
        page: String(page),
      });
      if (keyword.trim()) qs.set('q', keyword.trim());
      if (channel) qs.set('channel', channel);
      const res = await adminFetch(`/admin/v1/notification/templates?${qs.toString()}`);
      if (!res.ok) throw new Error(`템플릿 검색 실패 (${res.status})`);
      const env = (await res.json()) as ListEnvelope;
      const d = env.data;
      const items = (d?.items ?? []).map(toOption);
      const total = d?.totalElements;
      const hasMore =
        total != null
          ? (page + 1) * PAGE_SIZE < total
          : items.length === PAGE_SIZE;
      return total != null ? { items, hasMore, total } : { items, hasMore };
    },
    [channel],
  );

  const resolveOption = useCallback(
    async (templateCode: string): Promise<SearchSelectOption | null> => {
      const qs = new URLSearchParams({ q: templateCode, size: '1' });
      if (channel) qs.set('channel', channel);
      const res = await adminFetch(`/admin/v1/notification/templates?${qs.toString()}`);
      if (!res.ok) return null;
      const env = (await res.json()) as ListEnvelope;
      const match = (env.data?.items ?? []).find((t) => t.templateCode === templateCode);
      return match ? toOption(match) : { value: templateCode, label: templateCode };
    },
    [channel],
  );

  return (
    <SearchSelectField
      value={value}
      onChange={onChange}
      loadOptions={loadOptions}
      resolveOption={resolveOption}
      placeholder={placeholder ?? '템플릿 코드 검색'}
    />
  );
};
